import type { Weapon } from "../engine/weapon";
import { Solidifier } from "../engine/weapons/solidifier";
import { TripleSolidifier } from "../engine/weapons/tripleSolidifier";
import { Cannon } from "../engine/weapons/cannon";
import { CannonBurst } from "../engine/weapons/cannonBurst";
import { DrillLauncher } from "../engine/weapons/drillLauncher";
import { MissileGun } from "../engine/weapons/missileGun";
import { MissileBurst } from "../engine/weapons/missileBurst";
import { PlasmaGun } from "../engine/weapons/plasmaGun";
import { LandmineWeapon } from "../engine/weapons/landmineWeapon";
import { BarrierGun } from "../engine/weapons/barrierGun";
import { Flamethrower } from "../engine/weapons/flamethrower";
import { LaserGun } from "../engine/weapons/laserGun";
import { GasBombWeapon } from "../engine/weapons/gasBombWeapon";
import { OpGun } from "../engine/weapons/opGun";
import { OpGun2 } from "../engine/weapons/opGun2";
import { OpGunNuke } from "../engine/weapons/opGunNuke";
import { BlackHoleGun } from "../engine/weapons/blackHoleGun";
import { DevGunAutoKill } from "../engine/weapons/devGunAutoKill";
import { RockGun } from "../engine/weapons/rockGun";
import { CryoGun } from "../engine/weapons/cryoGun";
import { AirBlast } from "../engine/weapons/airBlast";
import { Jetpack } from "../engine/weapons/jetpack";
import { Sword } from "../engine/weapons/sword";
import { Cleaver } from "../engine/weapons/cleaver";
import { Key } from "../engine/items/key";
import { Medikit } from "../engine/items/medikit";
import { PowerPlantKeyCard } from "../engine/items/powerPlantKeyCard";
import { BaseKeyCard } from "../engine/items/baseKeyCard";
import { NightVisionEquipment } from "../engine/items/nightVisionEquipment";

/**
 * Maps weapon classes to the PNG filename of their projectile/effect sprite.
 * Uses the east-facing first frame from the tileset config (src/ui/tilesetConfig/data) where available.
 * DebugRemoteControl is intentionally unmapped (no visual projectile or effect).
 */
type WeaponConstructor = new (...args: never[]) => Weapon;

const weaponIconMap: Map<WeaponConstructor, string> = new Map([
	[Solidifier, "GreenProjectile_E_0.png"],
	[TripleSolidifier, "TripleGreenProjectile.png"],
	[Cannon, "Bullet_E.png"],
	[CannonBurst, "Bullet_Burst.png"],
	[DrillLauncher, "DrillProjectile_0.png"],
	[MissileGun, "Missile_E.png"],
	[MissileBurst, "Missile_Burst.png"],
	[PlasmaGun, "Shock_Projectile_2.png"],
	[LandmineWeapon, "MineOff.png"],
	[BarrierGun, "Barrier1a.png"],
	[Flamethrower, "Fire_0.png"],
	[LaserGun, "Laser_WE_2.png"],
	[GasBombWeapon, "GasBomb.png"],
	[OpGun, "Laser_WE_2.png"],
	[OpGun2, "BlueExplosion_0.png"],
	[OpGunNuke, "BlueExplosion_0.png"],
	[BlackHoleGun, "BlackHole-1x1.png"],
	[DevGunAutoKill, "WhiteProjectile.png"],
	[RockGun, "Rock.png"],
	[CryoGun, "IceProjectile_E_0.png"],
	[AirBlast, "WhiteProjectile.png"],
	[Jetpack, "Jetpack.png"],
	[Sword, "Shrapnel.png"],
	[Cleaver, "Cleaver.png"],
]);

/**
 * Item icons keyed by the item's static `key` string.
 * Used for loot popups and other pickup UI surfaces.
 */
const itemIconByKey: Map<string, string> = new Map([
	[Key.key, "Key.png"],
	[Medikit.key, "Medikit_0.png"],
	[PowerPlantKeyCard.key, "KeyCard.png"],
	[BaseKeyCard.key, "KeyCard.png"],
	[NightVisionEquipment.key, "NightVisionItem.png"],
	["GoldDrop", "GoldCoin_5.png"],
]);

/**
 * Returns the PNG filename for the icon representing a weapon's projectile or effect.
 * Returns null if no icon is available for the weapon.
 */
export function getPickupIconFilename(weapon: Weapon): string | null {
	return weaponIconMap.get(weapon.constructor as WeaponConstructor) ?? null;
}

/**
 * Reverse lookup map from weapon static key to icon filename.
 * Built once at module load from the weaponIconMap entries.
 */
const weaponIconByKey: Map<string, string> = new Map();
for (const [ctor, filename] of weaponIconMap) {
	weaponIconByKey.set((ctor as any).key, filename);
}

/**
 * Returns the PNG filename for a pickup icon by its static key string.
 * Resolves item pickups (Key, Medikit) first, then falls back to weapon icons.
 * Returns null if no icon is available for the key.
 */
export function getPickupIconFilenameByKey(pickupKey: string): string | null {
	const itemIcon = itemIconByKey.get(pickupKey);
	if (itemIcon) return itemIcon;
	return weaponIconByKey.get(pickupKey) ?? null;
}

/** Every icon filename this map can resolve — for asset-existence validation. */
export function getAllPickupIconFilenames(): string[] {
	return [...new Set([...weaponIconMap.values(), ...itemIconByKey.values()])];
}
